import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MaterialCategory } from '@/types';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export type { MaterialCategory };

export interface DatabaseMaterial {
  id: string;
  /** NULL = materiale del catalogo globale/condiviso (non modificabile/eliminabile dalle org). */
  organization_id: string | null;
  /** Famiglia di prodotto del fornitore (per sconti famiglia). NULL = non collegato. */
  family_id: string | null;
  /** Codice famiglia dalla view (es. "RP01"), NULL se senza famiglia. */
  family_code: string | null;
  category: MaterialCategory;
  code: string;
  name: string;
  description: string | null;
  supplier: string;
  thickness: number | null;
  width: number | null;
  length: number | null;
  weight_per_sqm: number | null;
  thermal_conductivity: number | null;
  acoustic_performance: number | null;
  fire_resistance_class: string | null;
  color_hex: string | null;
  unit_price: number;
  list_price: number | null;
  unit: string;
  installation_time_per_sqm: number | null;
  incidence_base: number | null;
  incidence_per_sqm: number | null;
  passo: number | null;
  // Nuovi campi specifici per le lastre
  material_type: string | null;
  board_typology: string | null;
  density: number | null;
  flexural_strength: string | null;
  surface_hardness: string | null;
  en_520_type: string | null;
  water_absorption: string | null;
  humidity_resistance_class: string | null;
  environmental_certification: string | null;
  recycled_content: number | null;
  voc_class: string | null;
  rei_compatible: boolean | null;
  intended_use: string[] | null;
  installation_notes: string | null;
  fire_class: string | null;
  fire_description: string | null;
  board_type: string | null;
  fire_usage_notes: string | null;
  // Nuovi campi specifici per montanti
  sheet_thickness: number | null;
  weight_per_ml: number | null;
  profile_type: string | null;
  surface_finish: string | null;
  // AGGIUNGIAMO SCONTO - ora è una stringa per supportare sconti multipli
  discount: string | null;
  // -- NUOVI CAMPI per categoria Altro --
  is_variable_thickness?: boolean | null;
  mechanical_performance?: string | null;
  thermal_performance_notes?: string | null;
  sustainability_notes?: string | null;
  system_compatibility?: string | null;
  fire_performance_notes?: string | null;
  carbon_footprint?: string | null;
  epd?: string | null;
  vapor_permeability?: string | null;
  thermal_capacity?: string | null;
  /** Aggiungiamo box_pieces qui per viti */
  box_pieces: number | null;
  /** Aggiungiamo compatible_board_types per viti */
  compatible_board_types: string[] | null;
  /** Percentuale sfrido */
  waste_percentage: number | null;
  /** Percentuale discarica */
  disposal_percentage: number | null;
  /** Override per-org (dalla vista materials_with_pricing). NULL = eredita. */
  custom_waste_percentage?: number | null;
  custom_disposal_percentage?: number | null;
  /** Valore di catalogo, prima dell'override della tua org. */
  catalog_waste_percentage?: number | null;
  catalog_disposal_percentage?: number | null;
  /** Sconto extra del singolo prodotto (%), applicato dopo la catena famiglia */
  extra_discount: number | null;
  // ---- Pricing dalla view materials_with_pricing (sola lettura) ----
  /** Catena cumulativa di sconti famiglia (es. [50, 54.5]) */
  family_discount_chain?: number[] | null;
  /** % netta della sola catena famiglia (es. 77.10 = paghi 22.90%) */
  family_discount_pct?: number | null;
  /** % extra del prodotto (uguale a extra_discount) */
  extra_discount_pct?: number | null;
  /** % totale combinata famiglia + extra */
  total_discount_pct?: number | null;
  /** Prezzo netto live (list × catena famiglia × (1 - extra/100)) */
  net_price?: number | null;
  /** True se la famiglia ha uno sconto attivo per questa org */
  has_family_discount?: boolean;
  /** True se il prodotto ha uno sconto extra > 0 */
  has_extra_discount?: boolean;
  // ---- Personalizzazioni per-org (sola lettura dalla view) ----
  /** Id della riga org_material_customizations, se l'org ha personalizzato questo materiale */
  customization_id?: string | null;
  /** Prezzo netto deciso dall'org (vince su listino × sconto). NULL = nessun override */
  custom_net_price?: number | null;
  /** Tempo di posa deciso dall'org, in ORE (la UI mostra i minuti) */
  custom_installation_time_per_sqm?: number | null;
  /** True se il prezzo mostrato è quello personalizzato dall'org */
  has_price_override?: boolean;
  /** True se il tempo di posa mostrato è quello personalizzato dall'org */
  has_time_override?: boolean;
  /** True se sfrido/smaltimento mostrati sono quelli personalizzati dall'org */
  has_waste_override?: boolean;
  has_disposal_override?: boolean;
  /** Prezzo da catalogo (listino × sconti) ignorando la personalizzazione */
  catalog_net_price?: number | null;
  /** Tempo di posa da catalogo in ORE, ignorando la personalizzazione */
  catalog_installation_time_per_sqm?: number | null;
  /** Famiglia scelta dall'org (gli sconti seguono questa). NULL = quella di catalogo */
  custom_family_id?: string | null;
  /** Famiglia effettivamente usata per gli sconti (custom se presente) */
  effective_family_id?: string | null;
  /** True se l'org ha spostato il materiale in una propria famiglia */
  has_family_override?: boolean;
}

export const useMaterials = () => {
  const { currentOrganizationId } = useCurrentOrganization();

  return useQuery({
    queryKey: ['materials', currentOrganizationId ?? 'global-only'],
    queryFn: async () => {
      // PostgREST max-rows=1000 server-side (Supabase default, non modificabile da dashboard
      // su piano Free/Pro). `.range(0, 9999)` da solo NON aiuta — il server tronca a 1000.
      // Soluzione: paginazione client-side in loop con chunks da 1000 finché .data.length < 1000.
      const CHUNK = 1000;
      const allRows: unknown[] = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let query = supabase
          .from('materials_with_pricing' as never)
          .select('*')
          .eq('is_active', true)
          .order('category', { ascending: true })
          .order('name', { ascending: true })
          .range(from, from + CHUNK - 1);

        if (currentOrganizationId) {
          query = query.or(`organization_id.is.null,organization_id.eq.${currentOrganizationId}`);
        } else {
          query = query.is('organization_id', null);
        }

        const { data, error } = await query;
        if (error) throw new Error(`Error fetching materials: ${error.message}`);
        const chunk = (data ?? []) as unknown[];
        allRows.push(...chunk);
        if (chunk.length < CHUNK) break;
        from += CHUNK;
        // Safety: max 20k materiali totali (20 iterazioni)
        if (from >= 20000) break;
      }
      // BUG FIX: la view materials_with_pricing fa LEFT JOIN su customer_discounts senza
      // filtrare per organization_id, quindi se piu' org hanno uno sconto sulla stessa famiglia
      // il materiale viene duplicato N volte (una per ogni discount attivo). Conseguenza:
      // counter sbagliato + sconti di altre org applicati al prezzo + key collision in React.
      // Dedup: per ogni id preferisci la riga col discount della tua org, poi quella globale,
      // poi qualsiasi (fallback).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dedupMap = new Map<string, any>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const row of allRows as any[]) {
        const existing = dedupMap.get(row.id);
        if (!existing) {
          dedupMap.set(row.id, row);
          continue;
        }
        const existingMatchesOrg = currentOrganizationId && existing.discount_org_id === currentOrganizationId;
        const rowMatchesOrg = currentOrganizationId && row.discount_org_id === currentOrganizationId;
        if (rowMatchesOrg && !existingMatchesOrg) {
          dedupMap.set(row.id, row);
          continue;
        }
        const existingIsGlobal = existing.discount_org_id == null;
        const rowIsGlobal = row.discount_org_id == null;
        if (!existingMatchesOrg && rowIsGlobal && !existingIsGlobal) {
          dedupMap.set(row.id, row);
        }
      }
      return Array.from(dedupMap.values()) as DatabaseMaterial[];
    },
  });
};

export const useMaterialsByCategory = (category: MaterialCategory) => {
  const { currentOrganizationId } = useCurrentOrganization();

  return useQuery({
    queryKey: ['materials', category, currentOrganizationId ?? 'global-only'],
    queryFn: async () => {
      // Paginazione client per superare PostgREST max-rows=1000 (vedi useMaterials sopra)
      const CHUNK = 1000;
      const allRows: unknown[] = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let query = supabase
          .from('materials_with_pricing' as never)
          .select('*')
          .eq('is_active', true)
          .eq('category', category as any)
          .order('name', { ascending: true })
          .range(from, from + CHUNK - 1);

        if (currentOrganizationId) {
          query = query.or(`organization_id.is.null,organization_id.eq.${currentOrganizationId}`);
        } else {
          query = query.is('organization_id', null);
        }

        const { data, error } = await query;
        if (error) throw new Error(`Error fetching materials for category ${category}: ${error.message}`);
        const chunk = (data ?? []) as unknown[];
        allRows.push(...chunk);
        if (chunk.length < CHUNK) break;
        from += CHUNK;
        if (from >= 20000) break;
      }
      // Vedi note su useMaterials: la view duplica per LEFT JOIN su customer_discounts.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dedupMap = new Map<string, any>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const row of allRows as any[]) {
        const existing = dedupMap.get(row.id);
        if (!existing) { dedupMap.set(row.id, row); continue; }
        const existingMatchesOrg = currentOrganizationId && existing.discount_org_id === currentOrganizationId;
        const rowMatchesOrg = currentOrganizationId && row.discount_org_id === currentOrganizationId;
        if (rowMatchesOrg && !existingMatchesOrg) { dedupMap.set(row.id, row); continue; }
        const existingIsGlobal = existing.discount_org_id == null;
        const rowIsGlobal = row.discount_org_id == null;
        if (!existingMatchesOrg && rowIsGlobal && !existingIsGlobal) dedupMap.set(row.id, row);
      }
      return Array.from(dedupMap.values()) as DatabaseMaterial[];
    },
  });
};
